import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined"
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined"
import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"

import { trimLongString } from "../../../common/utils"

const BADGE_VARIANTS = {
  member: {
    caption: "Team member",
    tooltip: (ownerName) =>
      `You're signed in as a team member on ${ownerName}'s OneBase account.`,
    ariaLabel: (ownerName) => `Team member on ${ownerName}'s account`,
    Icon: GroupsOutlinedIcon,
  },
  executive_caller: {
    caption: "SmartSetter EC",
    tooltip: (ownerName) =>
      `You're working on ${ownerName}'s OneBase account as a SmartSetter Executive Caller.`,
    ariaLabel: (ownerName) =>
      `SmartSetter Executive Caller on ${ownerName}'s account`,
    Icon: SupportAgentOutlinedIcon,
  },
  concierge: {
    caption: "Concierge",
    tooltip: (ownerName) =>
      `You're working on ${ownerName}'s OneBase account as a SmartSetter Concierge.`,
    ariaLabel: (ownerName) => `SmartSetter Concierge on ${ownerName}'s account`,
    Icon: RoomServiceOutlinedIcon,
  },
}

export default function MemberAccountBadge({ ownerName, variant = "member" }) {
  const shortName = trimLongString(ownerName, 20)
  const config = BADGE_VARIANTS[variant] || BADGE_VARIANTS.member
  const Icon = config.Icon

  return (
    <Tooltip title={config.tooltip(ownerName)} arrow placement="bottom">
      <BadgeRoot aria-label={config.ariaLabel(ownerName)}>
        <IconWrap>
          <Icon fontSize="inherit" />
        </IconWrap>

        <TextColumn sx={{ display: { xs: "none", sm: "flex" } }}>
          <CaptionLabel>{config.caption}</CaptionLabel>
          <OwnerName title={ownerName}>{shortName}&apos;s account</OwnerName>
        </TextColumn>

        <CompactLabel sx={{ display: { xs: "inline", sm: "none" } }}>
          {shortName}
        </CompactLabel>
      </BadgeRoot>
    </Tooltip>
  )
}

MemberAccountBadge.propTypes = {
  ownerName: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["member", "executive_caller", "concierge"]),
}

const BadgeRoot = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.25),
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${theme.palette.colors.brand50} 0%, rgba(255, 255, 255, 0.92) 100%)`,
  border: `1px solid ${theme.palette.colors.brand200}`,
  boxShadow:
    "0 8px 16px -4px rgba(119, 40, 164, 0.12), 0 2px 6px -2px rgba(16, 24, 40, 0.06)",
  color: theme.palette.colors.brand700,
  maxWidth: 240,
  flexShrink: 0,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: `linear-gradient(180deg, ${theme.palette.colors.brand500} 0%, ${theme.palette.colors.brand400} 100%)`,
    borderRadius: "12px 0 0 12px",
  },
  paddingLeft: theme.spacing(1.5),
}))

const IconWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "8px",
  flexShrink: 0,
  fontSize: "1rem",
  color: theme.palette.colors.brand600,
  backgroundColor: "rgba(255, 255, 255, 0.85)",
  border: `1px solid ${theme.palette.colors.brand200}`,
}))

const TextColumn = styled(Box)({
  flexDirection: "column",
  minWidth: 0,
  lineHeight: 1.2,
})

const CaptionLabel = styled(Typography)(({ theme }) => ({
  display: "block",
  fontSize: "0.625rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.colors.brand500,
  lineHeight: 1.3,
}))

const OwnerName = styled(Typography)({
  display: "block",
  fontSize: "0.8125rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.35,
})

const CompactLabel = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 88,
  lineHeight: 1.2,
})
